/**
 * Messages Page
 * Real-time chat interface with conversation list
 * Features: Read receipts, Typing indicators, Real-time polling
 */
import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuth, useLanguage, API } from "@/context/AppContext";
import { MessageSquare, Send, Loader2, Check, CheckCheck, Paperclip, Image as ImageIcon, FileText, X } from "lucide-react";

const MessagesPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [otherUserTyping, setOtherUserTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingSentRef = useRef(0);

  // Load conversations
  const loadConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/messages/conversations`, { withCredentials: true });
      setConversations(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load messages for a conversation
  const loadMessages = useCallback(async (convId) => {
    try {
      const res = await axios.get(`${API}/messages/${convId}`, { withCredentials: true });
      setMessages(res.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  // Check if other user is typing
  const checkTypingStatus = useCallback(async (convId) => {
    try {
      const res = await axios.get(`${API}/messages/typing/${convId}`, { withCredentials: true });
      setOtherUserTyping(res.data.is_typing);
    } catch (error) {
      setOtherUserTyping(false);
    }
  }, []);

  // Send typing indicator
  const sendTypingIndicator = useCallback(async (isTyping) => {
    if (!selectedConv) return;
    
    // Throttle typing indicator to every 2 seconds
    const now = Date.now();
    if (isTyping && now - lastTypingSentRef.current < 2000) return;
    lastTypingSentRef.current = now;

    try {
      await axios.post(`${API}/messages/typing`, {
        conversation_id: selectedConv.conversation_id,
        is_typing: isTyping
      }, { withCredentials: true });
    } catch (error) {
      // Silently fail typing indicator
    }
  }, [selectedConv]);

  // Initial load
  useEffect(() => {
    loadConversations();
    const convPoll = setInterval(loadConversations, 10000);
    return () => clearInterval(convPoll);
  }, [loadConversations]);

  // Polling when conversation is selected
  useEffect(() => {
    if (selectedConv) {
      loadMessages(selectedConv.conversation_id);
      
      // Poll for messages and typing status
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = setInterval(() => {
        loadMessages(selectedConv.conversation_id);
        checkTypingStatus(selectedConv.conversation_id);
      }, 2000);
    }
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [selectedConv, loadMessages, checkTypingStatus]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, otherUserTyping]);

  // Handle input change with typing indicator
  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    
    // Send typing indicator
    if (e.target.value.trim()) {
      setIsTyping(true);
      sendTypingIndicator(true);
      
      // Clear previous timeout
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      
      // Stop typing after 2 seconds of no input
      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(false);
        sendTypingIndicator(false);
      }, 2000);
    } else {
      setIsTyping(false);
      sendTypingIndicator(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConv || sending) return;

    // Stop typing indicator
    setIsTyping(false);
    sendTypingIndicator(false);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

    setSending(true);
    try {
      await axios.post(`${API}/messages`, {
        receiver_id: selectedConv.participant_id,
        content: newMessage.trim(),
        job_id: selectedConv.job_id
      }, { withCredentials: true });

      setNewMessage('');
      loadMessages(selectedConv.conversation_id);
      loadConversations();
    } catch (error) {
      toast.error('Nachricht konnte nicht gesendet werden');
    } finally {
      setSending(false);
    }
  };

  const handleAttachment = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so same file can be re-picked
    if (!file || !selectedConv) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Datei zu gross (max 10 MB)');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('receiver_id', selectedConv.participant_id);
    if (selectedConv.job_id) fd.append('job_id', selectedConv.job_id);
    setSending(true);
    try {
      await axios.post(`${API}/messages/attachment`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Anhang gesendet');
      loadMessages(selectedConv.conversation_id);
      loadConversations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setSending(false);
    }
  };

  const formatMessageTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) {
      return date.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Gestern ' + date.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
    } else if (days < 7) {
      return date.toLocaleDateString('de-CH', { weekday: 'short' }) + ' ' + 
             date.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit' });
    }
  };

  const getTotalUnread = () => {
    return conversations.reduce((acc, conv) => acc + (conv.unread_count || 0), 0);
  };

  // Typing indicator component
  const TypingIndicator = () => (
    <div className="flex justify-start">
      <div className="bg-gray-100 text-gray-600 rounded-2xl rounded-bl-md px-4 py-3 max-w-[70%]">
        <div className="flex items-center gap-1">
          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#0044CC]" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-8rem)]" data-testid="messages-page">
      <div className="flex items-center gap-3 mb-6">
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
          {t('messages')}
        </h1>
        {getTotalUnread() > 0 && (
          <Badge className="bg-red-500 text-white">{getTotalUnread()} neu</Badge>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[calc(100%-4rem)]">
        {/* Conversations List */}
        <Card className="border-gray-200 overflow-hidden">
          <ScrollArea className="h-full">
            {conversations.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p>{t('noConversations')}</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {conversations.map((conv) => (
                  <button
                    key={conv.conversation_id}
                    onClick={() => setSelectedConv(conv)}
                    className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                      selectedConv?.conversation_id === conv.conversation_id ? 'bg-blue-50 border-l-4 border-[#0044CC]' : ''
                    }`}
                    data-testid={`conversation-${conv.conversation_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={conv.participant_picture} />
                          <AvatarFallback className="bg-[#0044CC] text-white">
                            {conv.participant_name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        {conv.unread_count > 0 && (
                          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                            {conv.unread_count > 9 ? '9+' : conv.unread_count}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className={`font-medium truncate ${conv.unread_count > 0 ? 'text-black' : 'text-gray-700'}`}>
                            {conv.participant_name}
                          </p>
                          <span className="text-xs text-gray-400">
                            {formatMessageTime(conv.last_message_time)}
                          </span>
                        </div>
                        <p className={`text-sm truncate ${conv.unread_count > 0 ? 'text-gray-800 font-medium' : 'text-gray-500'}`}>
                          {conv.last_message}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </ScrollArea>
        </Card>

        {/* Messages */}
        <Card className="border-gray-200 md:col-span-2 flex flex-col overflow-hidden">
          {selectedConv ? (
            <>
              <div className="p-4 border-b border-gray-100 flex items-center gap-3 bg-gray-50">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={selectedConv.participant_picture} />
                  <AvatarFallback className="bg-[#0044CC] text-white">
                    {selectedConv.participant_name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold">{selectedConv.participant_name}</p>
                  {otherUserTyping ? (
                    <p className="text-xs text-[#0044CC] flex items-center gap-1 animate-pulse">
                      schreibt...
                    </p>
                  ) : (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                      Online
                    </p>
                  )}
                </div>
              </div>

              <ScrollArea className="flex-1 p-4 bg-gray-50/30">
                <div className="space-y-4">
                  {messages.map((msg, idx) => {
                    const isOwn = msg.sender_id === user?.user_id;
                    const showDate = idx === 0 || 
                      new Date(msg.created_at).toDateString() !== new Date(messages[idx-1]?.created_at).toDateString();
                    
                    // Check if this is the last message sent by current user
                    const isLastOwnMessage = isOwn && 
                      !messages.slice(idx + 1).some(m => m.sender_id === user?.user_id);
                    
                    return (
                      <div key={msg.message_id}>
                        {showDate && (
                          <div className="text-center my-4">
                            <span className="text-xs text-gray-400 bg-white px-3 py-1 rounded-full">
                              {new Date(msg.created_at).toLocaleDateString('de-CH', { 
                                weekday: 'long', day: 'numeric', month: 'long' 
                              })}
                            </span>
                          </div>
                        )}
                        <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                          <div
                            className={`max-w-[70%] px-4 py-3 shadow-sm ${
                              isOwn
                                ? 'bg-[#0044CC] text-white rounded-2xl rounded-br-md'
                                : 'bg-white text-gray-800 rounded-2xl rounded-bl-md border border-gray-100'
                            }`}
                          >
                            {msg.attachment_url && msg.attachment_type === 'image' ? (
                              <a href={msg.attachment_url} target="_blank" rel="noopener noreferrer">
                                <img src={msg.attachment_url} alt={msg.attachment_name || 'Anhang'} className="max-w-full max-h-64 rounded-xl mb-1 cursor-pointer" />
                              </a>
                            ) : msg.attachment_url ? (
                              <a href={msg.attachment_url} download={msg.attachment_name} className={`flex items-center gap-2 px-3 py-2 rounded-xl mb-1 ${isOwn ? 'bg-white/15' : 'bg-gray-100'}`}>
                                <FileText className="w-5 h-5 flex-shrink-0" />
                                <span className="text-xs truncate">{msg.attachment_name || 'Datei'}</span>
                              </a>
                            ) : null}
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            <div className={`flex items-center justify-end gap-1 mt-1 ${
                              isOwn ? 'text-blue-200' : 'text-gray-400'
                            }`}>
                              <span className="text-xs">
                                {new Date(msg.created_at).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                              {isOwn && (
                                <span className="text-xs ml-1" title={msg.is_read ? 'Gelesen' : 'Zugestellt'}>
                                  {msg.is_read ? (
                                    <CheckCheck className="w-4 h-4 inline text-blue-300" />
                                  ) : (
                                    <Check className="w-4 h-4 inline" />
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        {/* Show read receipt timestamp for last own message */}
                        {isOwn && isLastOwnMessage && msg.is_read && msg.read_at && (
                          <div className="text-right mt-1">
                            <span className="text-xs text-gray-400">
                              Gelesen {new Date(msg.read_at).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {/* Typing indicator */}
                  {otherUserTyping && <TypingIndicator />}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-100 bg-white">
                <input
                  type="file"
                  id="msg-attach"
                  className="hidden"
                  onChange={handleAttachment}
                  accept="image/*,.pdf,.doc,.docx,.txt"
                  data-testid="attachment-input"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => document.getElementById('msg-attach')?.click()}
                    className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors flex-shrink-0"
                    title={t('attachFile') || 'Datei anhängen'}
                    disabled={sending}
                    data-testid="attach-btn"
                  >
                    <Paperclip className="w-4 h-4 text-gray-600" />
                  </button>
                  <Input
                    value={newMessage}
                    onChange={handleInputChange}
                    placeholder={t('writeMessage')}
                    className="flex-1 rounded-full border-gray-200"
                    data-testid="message-input"
                    disabled={sending}
                  />
                  <Button 
                    type="submit" 
                    className="bg-[#0044CC] hover:bg-[#003399] text-white rounded-full px-6" 
                    data-testid="send-message-btn"
                    disabled={sending || !newMessage.trim()}
                  >
                    {sending ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-400 bg-gray-50/50">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg">{t('selectConversation')}</p>
                <p className="text-sm mt-2">Wählen Sie einen Chat aus der Liste</p>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default MessagesPage;
